/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";
import React, { useEffect, useRef, useState } from "react";
import SectionHeading from "../others/SectionHeading";
import Image from "next/image";
import { SchemePortfolioItem } from "@/type/schemeType";
import { formatDate } from "@/utils/commonFunctions";
import MarketCapChart from "../charts/MarketCapChart";
import AllSectorsAllocations from "../others/AllSectorsAllocations";
import AllCompaniesAllocations from "../others/AllCompaniesAllocations";
import { useAppDispatch } from "@/redux/Hooks";
import { setData as setStoreData } from "@/redux/features/StoreData/storeData";
import InformationButton from "@/components/ui/InformationButton";

const PALETTE = [
  "#657DFF",
  "#5ED5A8",
  "#FBBF24",
  "#FF6B6B",
  "#FFD93D",
  "#6BCB77",
  "#4D96FF",
  "#9333EA",
  "#06B6D4",
  "#9CA3AF",
  "#D97706",
  "#F472B6",
];

type ResultArray = {
  [key: string]: {
    company: SchemePortfolioItem[];
    sector: { instrument: string; holdings: number }[];
    market: { instrument: string; holdings: number }[];
    market_cap: { instrument: string; holdings: number }[];
  };
};

type TabItem = {
  name: string;
  allocation: number;
};

type TabList = TabItem[];

export default function PortfolioSections({
  tabList,
  portfolioData,
  numberOfStocks,
  updatedDate,
}: {
  tabList: TabList;
  portfolioData: ResultArray;
  numberOfStocks: string;
  updatedDate: Date | string | null;
}) {
  const [activeTab, setActiveTab] = useState(0);
  const dispatch = useAppDispatch();

  const colorCacheRef = useRef<Record<string, Record<string, string>>>({});

  const assetValues = tabList
    .filter((item) => item.allocation > 0)
    .map((item) => ({
      label: item.name,
      value: `${Number(item.allocation).toFixed(2)}%`,
    }));

  const activeTabName = tabList[activeTab]?.name || "";
  const activeKey = activeTabName.toLowerCase().replace(/ /g, "_").replace(/&/g, "and");

  if (!colorCacheRef.current[activeTabName]) {
    colorCacheRef.current[activeTabName] = {};
  }

  const activePortfolio = portfolioData[activeKey] || {
    company: [],
    sector: [],
    market: [],
    market_cap: [],
  };

  useEffect(() => {
    if (!portfolioData) return;

    const top5Sectors = portfolioData["equity"]?.sector?.slice(0, 5) || [];
    const top5Comp = portfolioData["equity"]?.company?.slice(0, 5) || [];

    const sectorInstrumentsString = top5Sectors.map((item: any) => item.instrument).join(", ");

    const companyInstrumentsString = top5Comp.map((item: any) => item.instrument).join(", ");

    dispatch(setStoreData({ key: "sectorInstruments", value: sectorInstrumentsString }));
    dispatch(setStoreData({ key: "companyInstruments", value: companyInstrumentsString }));
  }, [portfolioData]);

  const marketCapValues = (activePortfolio.market_cap || [])
    .filter((item) => item.holdings > 0)
    .map((item) => ({
      label: item.instrument,
      value: `${Number(item.holdings).toFixed(2)}%`,
    }));

  const companies = activePortfolio.company || [];
  // const top10Companies = [...companies].sort((a, b) => (b.holdings ?? 0) - (a.holdings ?? 0)).slice(0, 120);
  const top10Companies = [...companies]
    .filter((company) => (company.holdings ?? 0) > 0)
    .sort((a, b) => (b.holdings ?? 0) - (a.holdings ?? 0))
    .slice(0, 120);
    const top10Companies1 = [...companies]
    .filter((company) => (company.holdings ?? 0) > 0)
    .sort((a, b) => (b.holdings ?? 0) - (a.holdings ?? 0))
    .slice(0, 120)
  const totalTop10Holdings = top10Companies1.reduce((sum, sector) => sum + (sector.holdings ?? 0), 0);

  const stats = [
    {
      title: "Asset Allocation",
      values: assetValues,
      icon: "quicklook-asset-icon",
    },
    {
      title: "Market Cap",
      values: marketCapValues,
      icon: "quicklook-market-icon",
    },
    {
      title: "Number of stocks",
      values: [{ label: "", value: numberOfStocks.toString() || "-" }],
      icon: "quicklook-stocks-icon",
    },
    {
      title: "Top 10 Holdings",
      values: [{ label: "", value: `${totalTop10Holdings.toFixed(2)}% of portfolio` }],
      icon: "quicklook-holdings-icon",
    },
  ];

  const colorsForTab = colorCacheRef.current[activeTabName];
  let paletteIndex = 0;

  const chartData = (activePortfolio.market_cap || []).map((item) => {
    if (!colorsForTab[item.instrument]) {
      colorsForTab[item.instrument] = PALETTE[paletteIndex % PALETTE.length];
      paletteIndex++;
    }

    return {
      label: item.instrument,
      value: item.holdings,
    };
  });

  return (
    <>
      <div
        className={`flex items-center justify-between gap-3 xs:gap-7 p-1 overflow-x-auto scrollbar-none rounded-[100px] border border-[#1A51AA24] shadow-[0px_3px_5px_1px_#0000000D] overflow-hidden bg-[#EBF3FF] mb-3 sm:mb-4 ${tabList.length > 1 ? "w-full" : "w-fit mx-auto"}`}
      >
        {tabList.map((tab, index) => {
          const isActive = index === activeTab;
          return (
            <button
              key={index}
              onClick={() => setActiveTab(index)}
              className={`w-full max-w-96 text-center whitespace-nowrap text-sm sm:text-base rounded-[100px] px-4 py-1.5 transition-all duration-350 ease-in-out ${isActive ? "bg-white text-primaryBlue font-semibold" : "text-[#202020A6] hover:text-primaryBlue hover:bg-[#ffffff8f]"}`}
            >
              {tab.name}: {Number(tab.allocation).toFixed(2)}%
            </button>
          );
        })}
      </div>

      <section className="mb-8 xs:mb-12">
        <div className="mb-3 sm:mb-4 flex items-center gap-3">
          <SectionHeading heading="Portfolio Overview" />
          <InformationButton keywords="portfolio-overview" />
          <p className="text-2xs sm:text-sm text-[#6E6E73] font-medium ml-auto">
            As of {formatDate(updatedDate || "")}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {stats.map((item, i) => (
            <div key={i} className="flex items-start gap-3 rounded-2xl border border-gray-200 bg-white p-4 shadow-xs">
              <Image
                src={`/next_assets/images/scheme-details/icons/${item.icon}.svg`}
                alt="Icon Portfolio"
                width={44}
                height={44}
                priority
                className="h-8 w-8 sm:h-11 sm:w-11 shrink-0"
              />

              <div>
                <p className="text-sm text-[#4b4b4b] leading-4">{item.title}</p>

                <div className="text-base text-primaryBlack flex flex-wrap gap-x-3 mt-2 font-medium">
                  {item.values.map((v, idx) => (
                    <span key={idx}>
                      {v.label && <>{v.label}: </>}
                      <>{v.value}</>
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="mb-8 xs:mb-12">
        <div className="mb-3 sm:mb-4 flex items-center gap-3">
          <SectionHeading heading={`Market Cap of ${activeTabName}`} />
          <InformationButton keywords="market-cap-equity" />
        </div>

        <MarketCapChart
          totalValue={chartData.reduce((acc, cur) => acc + cur.value, 0)}
          data={chartData}
          chartName={tabList[activeTab]?.name || ""}
          className="p-6 bg-white rounded-4xl border border-[#E9E9EB] mb-5 sm:mb-0"
        />
      </section>

      {activeTabName.toLowerCase() != "equity" && (
        <section className="mb-8 xs:mb-12">
          <SectionHeading heading={`Companies Allocation of ${activeTabName}`} className="mb-3 sm:mb-4" />

          <AllCompaniesAllocations items={top10Companies} />
        </section>
      )}

      <section>
        <SectionHeading heading={`Sector Allocation of ${activeTabName}`} className="mb-3 sm:mb-4" />

        <AllSectorsAllocations items={portfolioData[activeTabName.toLowerCase()].sector} />
      </section>
    </>
  );
}
